'use client';

import { useTheme } from '@emotion/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Container } from '../../layout/Container';
import {
  DEFAULT_SEO_DESCRIPTION,
  DEFAULT_SEO_TITLE,
  DEVICE_SCREENS_MAX_STRING,
} from '../../../lib/consts';
import { BreadCrumbs } from '../../blocks/BreadCrumbs';
import { H1 } from '../../styled/typography';
import LoupSVG from '../../../public/img/loupe.svg';
import { MobileApps } from '../../about/MobileApps';
import { Subscription } from '../../about/Subscription';
import { ButtonShowMore } from '../../Buttons';
import { StandardButton } from '../../styled/Button';
import { PlaceCard } from '../../places/place-card';
import { http } from '../../../lib/api/http';
import { V2 } from '../../../lib/api/urls';
import { activeStatus } from '../../../constants/events';
import FilterSVG from '../../../public/img/filter.svg';
import { PlaceFilter } from '../../filter/PlaceFilter';
import { City, Place } from '../../../models';
import { Category } from '../../../models/categories';
import { GB_DOMAIN } from '../../../lib/api/apiUrl';
import { extractUtmParams } from '../../../utils/utm';
import { useCookieAgreement } from '../../../providers';
import { objectToSearchParams } from '../../../utils/objectToSearchParams';
import { useLoading } from '../../../providers/loading';
import { withSuspense } from '../../../utils/withSuspense';
import { getPlaces } from '../../../lib/services/backend';

function CatalogPlacesPageInner({
  currentCity,
  placesList,
  categories,
  startQuery,
}: {
  currentCity: City;
  placesList: { results: Place[]; next: string | null };
  categories: { results: Category[]; count: number; next: null | string };
  startQuery: { pages: number[]; categories: Category[]; category?: string };
}) {
  const LIMIT_CARDS = 6;
  const { showPopUp } = useCookieAgreement();
  const theme = useTheme();

  const router = useRouter();
  const searchParams = useSearchParams();

  const [places, setPlaces] = useState(placesList);
  const [isNewData, setIsNewData] = useState(false);
  const [query, setQuery] = useState(startQuery);
  const [showFilter, setShowFilter] = useState(false);
  const { updateLoading } = useLoading();

  const onCheckCategory = (category: Category) => {
    const categoriesList = [...query.categories];
    if (categoriesList.find((item) => item.id === category.id)) {
      const index = categoriesList.findIndex((item) => item.id === category.id);
      categoriesList.splice(index, 1);
    } else {
      categoriesList.push(category);
    }
    const newCategory = categoriesList[0]
      ? query.category
        ? query.category !== categoriesList[0].slug
          ? categoriesList[0].slug
          : query.category
        : categoriesList[0].slug
      : '';
    setQuery({
      ...query,
      categories: categoriesList,
      category: newCategory,
    });
  };

  const getSeoParams = () => {
    if (query.categories.length === 1) {
      return {
        seoTitle: query.categories[0].seo_title || DEFAULT_SEO_TITLE,
        seoDescription:
          query.categories[0].seo_description || DEFAULT_SEO_DESCRIPTION,
        h1: query.categories[0].title,
        canonicalLink: `${GB_DOMAIN}/${currentCity.url}/place-catalog/${query.categories[0].slug}/`,
      };
    }
    return {
      seoTitle: DEFAULT_SEO_TITLE,
      seoDescription: DEFAULT_SEO_DESCRIPTION,
      h1: 'Каталог мест',
      canonicalLink: `${GB_DOMAIN}/${currentCity.url}/place-catalog/`,
    };
  };

  useEffect(() => {
    const utmParams = extractUtmParams(searchParams);
    let pathname = `/${currentCity.url}/place-catalog/`;
    if (query.category) {
      pathname += `${query.category}/`;
    }

    router.push(
      `${pathname}?${objectToSearchParams({
        categories: query.categories.map((item: Category) => item.slug),
        page: query.pages,
        ...utmParams.utmObject,
      }).toString()}`
    );

    updateLoading(true);

    getPlaces({
      limit:
        query.pages.length === 2
          ? isNewData
            ? LIMIT_CARDS
            : (query.pages[1] - query.pages[0] + 1) * LIMIT_CARDS
          : LIMIT_CARDS,
      offset: query.pages[0]
        ? isNewData
          ? (query.pages[1] - 1) * LIMIT_CARDS
          : (query.pages[0] - 1) * LIMIT_CARDS
        : 0,
      is_web: true,
      status: activeStatus,
      city: currentCity.remote_id,
      ordering: '-views',
      categories: query.categories.map((category: Category) => category.id),
    })
      .then((res) => {
        if (!isNewData) {
          setPlaces(res);
        } else {
          setPlaces({
            ...res,
            results: places.results.concat(res.results),
          });
        }
      })
      .finally(() => updateLoading(false));

    setIsNewData(false);
  }, [query]);

  useEffect(() => {
    setTimeout(
      () => window.scrollTo({ top: 0, left: 0, behavior: 'instant' }),
      0
    );
  }, [showFilter]);

  const { h1, canonicalLink, seoDescription, seoTitle } = getSeoParams();

  useEffect(() => {
    document.title = seoTitle;
  }, [seoTitle]);

  return (
    <Container
      css={{
        padding: '15px 12px',
        [DEVICE_SCREENS_MAX_STRING.md]: {
          padding: '15px 0',
          paddingBottom: 80,
        },
      }}
    >
      <BreadCrumbs
        currentCity={currentCity}
        items={[{ title: 'Каталог мест' }]}
      />
      <H1
        css={{
          color: theme.colors.text,
          fontWeight: 700,
          fontSize: 32,
          margin: 0,
          marginTop: 21,
          marginBottom: '4rem',
          hyphens: 'auto',
          [DEVICE_SCREENS_MAX_STRING.md]: {
            lineHeight: '26px',
            marginBottom: '2rem',
            padding: '0 8px 0 8px',
          },
          fontFamily: 'Montserrat',
          lineHeight: '38px',
          letterSpacing: '0.01em',
        }}
      >
        {h1}
      </H1>
      <main css={{ fontFamily: 'Golos', color: theme.colors.black }}>
        <div
          css={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            paddingTop: '1rem',
            [DEVICE_SCREENS_MAX_STRING.lg]: {
              flexDirection: 'column',
            },
          }}
        >
          <div
            css={{
              display: 'block',
              width: '25%',
              [DEVICE_SCREENS_MAX_STRING.lg]: {
                width: '100%',
              },
              [DEVICE_SCREENS_MAX_STRING.md]: {
                display: 'none',
              },
            }}
          >
            <PlaceFilter
              categories={categories}
              query={query}
              onCheckCategory={onCheckCategory}
              currentCity={currentCity}
            />
          </div>
          {showFilter && (
            <PlaceFilter
              categories={categories}
              query={query}
              onCheckCategory={onCheckCategory}
              currentCity={currentCity}
            />
          )}
          {!showFilter && (
            <div
              css={{
                width: '75%',
                [DEVICE_SCREENS_MAX_STRING.lg]: {
                  width: '100%',
                },
              }}
            >
              {places.results && places.results.length ? (
                <div
                  css={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    [DEVICE_SCREENS_MAX_STRING.md]: {
                      gridTemplateColumns: '1fr 1fr',
                    },
                    [DEVICE_SCREENS_MAX_STRING.sm]: {
                      gridTemplateColumns: '1fr',
                    },
                  }}
                >
                  {places.results.map((item) => (
                    <PlaceCard key={`PlaceCard__${item.id}`} place={item} />
                  ))}
                </div>
              ) : (
                <div
                  css={{
                    fontFamily: 'Montserrat',
                    width: '100%',
                    minHeight: 400,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <LoupSVG css={{ marginBottom: 30 }} />
                  <div
                    css={{
                      marginBottom: 4,
                      fontSize: 18,
                      fontWeight: 'bold',
                      color: '#333',
                    }}
                  >
                    К сожалению, ничего не нашлось.
                  </div>
                  <div
                    css={{
                      fontSize: 12,
                      color: '#333',
                    }}
                  >
                    Попробуйте изменить варианты или фильтры поиска
                  </div>
                </div>
              )}
              {places.next && (
                <div
                  css={{
                    display: 'flex',
                    gap: 10,
                    padding: '0 8px',
                    marginTop: 10,
                  }}
                >
                  <ButtonShowMore
                    callback={() => {
                      setIsNewData(true);
                      setQuery({
                        ...query,
                        pages: query.pages[1]
                          ? [1, Number(query.pages[1]) + 1]
                          : [1, 2],
                      });
                    }}
                  />
                  <StandardButton callback={() => window.scrollTo(0, 0)}>
                    Наверх
                  </StandardButton>
                </div>
              )}
            </div>
          )}
        </div>
        <div
          css={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        />
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            padding: '0 8px',
            marginBottom: '4rem',
            marginTop: 80,
            [DEVICE_SCREENS_MAX_STRING.xl]: {
              gridTemplateColumns: '1fr',
            },
            [DEVICE_SCREENS_MAX_STRING.md]: {
              display: 'none',
            },
          }}
        >
          <Subscription currentCity={currentCity} />
          <MobileApps />
        </div>

        <div
          css={{
            display: 'none',
            width: '100%',
            justifyContent: 'center',
            position: 'fixed',
            left: 0,
            bottom: showPopUp ? 67 : 15,
            zIndex: 100,
            [DEVICE_SCREENS_MAX_STRING.md]: {
              display: 'flex',
            },
          }}
        >
          {showFilter ? (
            <button
              css={{
                width: '100%',
                backgroundColor: '#ff4a2e',
                backgroundImage:
                  'linear-gradient(120deg, #ff4a2e 0%, #de2839 100%)',
                margin: '16px 15px',
                padding: '5px 0',
                borderRadius: 20,
                fontSize: 16,
                color: '#fff',
              }}
              type='button'
              onClick={() => setShowFilter(false)}
            >
              Найти
            </button>
          ) : (
            <button
              css={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '11px 14px',
                backgroundColor: '#ff4a2e',
                backgroundImage:
                  'linear-gradient(120deg, #ff4a2e 0%, #de2839 100%)',
                color: '#fff',
                borderRadius: '10px',
                fontSize: 12,
                fontWeight: 500,
              }}
              type='button'
              onClick={() => setShowFilter(true)}
            >
              Фильтр
              <FilterSVG css={{ width: 16, height: 16, fill: '#fff' }} />
            </button>
          )}
        </div>
      </main>
    </Container>
  );
}

export const CatalogPlacesPage = withSuspense(CatalogPlacesPageInner);
